import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, SearchIcon, ShieldCheckIcon, SparklesIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { NETWORKING_GUESTS, NetworkingGuest } from '../data/networking-guests';
import { GOLD, NETWORKING_COPY } from '../data/networking-copy';
import { GuestCard } from '../components/networking/GuestCard';
import { IntroRequestDialog } from '../components/networking/IntroRequestDialog';

/** Cards rendered up front, then appended in batches as the visitor scrolls. */
const PAGE_SIZE = 24;
export function VoicesOfImpactNetworkingPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isEs = language === 'ES';
  const c = isEs ? NETWORKING_COPY.ES : NETWORKING_COPY.EN;
  const [query, setQuery] = useState('');
  const [sector, setSector] = useState<string>('');
  const [activeGuest, setActiveGuest] = useState<NetworkingGuest | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // The input stays instant while filtering settles, so typing never waits on
  // a re-render of the full directory.
  const [deferredQuery, setDeferredQuery] = useState('');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const id = window.setTimeout(() => setDeferredQuery(query), 120);
    return () => window.clearTimeout(id);
  }, [query]);
  const openRequest = useCallback((guest: NetworkingGuest) => {
    setActiveGuest(guest);
  }, []);
  const sectors = useMemo(() => Array.from(new Set(NETWORKING_GUESTS.flatMap(g => [g.sector, g.secondarySector, ...(g.extraSectors ?? [])].filter(Boolean)) as string[])).sort((a, b) => a.localeCompare(b)), []);
  const guests = useMemo(() => {
    const q = deferredQuery.trim().toLowerCase();
    const matches = NETWORKING_GUESTS.filter(g => {
      if (sector && g.sector !== sector && g.secondarySector !== sector && !(g.extraSectors ?? []).includes(sector)) return false;
      if (!q) return true;
      return [g.name, g.title, g.organization, g.location, g.bio].filter(Boolean).some(field => (field as string).toLowerCase().includes(q));
    });

    // HAA board members lead the directory, then the evening's featured guests.
    const pinned = matches.filter(g => g.boardOrder !== undefined || g.featuredOrder !== undefined).sort((a, b) => (a.boardOrder ?? Infinity) - (b.boardOrder ?? Infinity) || (a.featuredOrder ?? Infinity) - (b.featuredOrder ?? Infinity));

    // Everyone else is interleaved across industries so the grid reads as a
    // mix of fields rather than one long block per sector. Guests without a
    // portrait trail the photographed ones so the grid stays visually solid.
    const unpinned = matches.filter(g => g.boardOrder === undefined && g.featuredOrder === undefined);
    const rest = unpinned.filter(g => Boolean(g.photo));
    const withoutPhoto = unpinned.filter(g => !g.photo).sort((a, b) => a.name.localeCompare(b.name));
    const bySector = new Map<string, NetworkingGuest[]>();
    rest.slice().sort((a, b) => a.name.localeCompare(b.name)).forEach(g => {
      const bucket = bySector.get(g.sector);
      if (bucket) bucket.push(g);else bySector.set(g.sector, [g]);
    });
    const buckets = Array.from(bySector.keys()).sort((a, b) => a.localeCompare(b)).map(key => bySector.get(key) as NetworkingGuest[]);
    const interleaved: NetworkingGuest[] = [];
    for (let row = 0; interleaved.length < rest.length; row++) {
      buckets.forEach(bucket => {
        if (bucket[row]) interleaved.push(bucket[row]);
      });
    }

    // Guests with `placeAfter` sit directly behind the named guest when both
    // are in the current results; otherwise they keep their natural spot.
    const ordered = [...pinned, ...interleaved, ...withoutPhoto];
    const presentIds = new Set(ordered.map(g => g.id));
    const followers = ordered.filter(g => g.placeAfter && presentIds.has(g.placeAfter));
    if (followers.length === 0) return ordered;
    const base = ordered.filter(g => !followers.includes(g));
    followers.forEach(f => {
      const idx = base.findIndex(g => g.id === f.placeAfter);
      base.splice(idx + 1, 0, f);
    });
    return base;
  }, [deferredQuery, sector]);

  // A new search or sector resets the grid to the first batch.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [deferredQuery, sector]);
  const shown = useMemo(() => guests.slice(0, visibleCount), [guests, visibleCount]);
  const hasMore = visibleCount < guests.length;

  // Appending on intersection keeps the initial render small on a long roster.
  const sentinel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setVisibleCount(n => n + PAGE_SIZE);
      }
    }, {
      rootMargin: '600px 0px'
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, guests.length]);
  const chip = (active: boolean) => `px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.12em] border transition-colors duration-150 ${active ? 'text-[#0b1f3f] border-transparent' : 'text-white/60 border-white/15 hover:border-white/35 hover:text-white'}`;
  return <div className="min-h-screen w-full bg-[#0b1f3f] text-white font-nunito" data-id="element-4054" data-mp="34mcd1d09eqeh">
      <header className="relative z-10 w-full px-6 py-6 sm:px-12 flex items-center justify-between gap-4 border-b border-[#c9a227]/30" data-id="element-4055" data-mp="34ofd6pv4lzwx">
        <a href="#/voices-of-impact-ny" className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 transition-colors hover:text-white" data-id="element-4056" data-mp="37ogwlm1eywle">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-4057" data-mp="3goviamiaidpf" />
          {c.back}
        </a>
        <button onClick={() => setLanguage(isEs ? 'EN' : 'ES')} className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.14em] border transition-colors duration-150 hover:bg-[#c9a227] hover:text-[#0b1f3f]" style={{
        borderColor: `${GOLD}66`,
        color: GOLD
      }} data-id="element-4058" data-mp="3gmj66pz7qxka">
          {isEs ? 'English' : 'Español'}
        </button>
      </header>

      <main data-id="element-4059" data-mp="2xwtb01rjg9ru">
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pt-14 pb-10" data-id="element-4060" data-mp="2xwb4mcb5wf67">
          <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4" style={{
          color: GOLD
        }} data-id="element-4061" data-mp="32q5mrbb4cm36">
            {c.eyebrow}
          </p>
          <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-wider leading-none mb-6" data-id="element-4062" data-mp="2a7i7icit01a0">
            {c.title}
          </h1>
          <p className="text-base sm:text-lg leading-relaxed text-white/70 max-w-3xl" data-id="element-4063" data-mp="22mocxk9vhvzr">
            {c.intro}
          </p>
          <div className="mt-6 inline-flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 max-w-2xl" data-id="element-4064" data-mp="1sql1fnt4sl05">
            <ShieldCheckIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
            color: GOLD
          }} data-id="element-4065" data-mp="1nqylh6lx4qnn" />
            <p className="text-sm leading-relaxed text-white/60" data-id="element-4066" data-mp="1js98vg8pfd52">{c.privacy}</p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pb-6" data-id="element-4067" data-mp="1a0d1fekqaao6">
          <div className="flex flex-col gap-5" data-id="element-4068" data-mp="19y8zh26e3lqj">
            <div className="relative w-full max-w-3xl" data-id="element-4069" data-mp="1521r72qity5f">
              <SearchIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-white/45" data-id="element-4070" data-mp="14z5vzm625nko" />
              <input type="search" value={query} onChange={e => setQuery(e.target.value)} aria-label={c.searchLabel} placeholder={c.searchPlaceholder} className="w-full rounded-full bg-white/5 border border-white/20 pl-14 pr-5 py-4 text-base text-white placeholder-white/40 outline-none transition-colors focus:border-[#c9a227] focus:bg-white/10" data-id="element-4071" data-mp="10409om8qu6w2" />
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-4072" data-mp="2ewcc702xtfw3">
              <button onClick={() => setSector('')} className={chip(sector === '')} style={sector === '' ? {
              backgroundColor: GOLD
            } : undefined} data-id="element-4073" data-mp="2168u8lb3c8cg">
                {c.all}
              </button>
              {sectors.map(s => <button key={s} onClick={() => setSector(s)} className={chip(sector === s)} style={sector === s ? {
              backgroundColor: GOLD
            } : undefined} data-id="element-4074" data-mp="1w761o0caruld">
                  {s}
                </button>)}
            </div>
          </div>
          <p className="mt-4 text-xs font-bold uppercase tracking-[0.18em] text-white/40" data-id="element-4075" data-mp="13m0plqxi4wox">
            {guests.length} {guests.length === 1 ? c.countOne : c.countMany}
          </p>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pb-20" data-id="element-4076" data-mp="1cngxns0ugmpz">
          {guests.length === 0 ? <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-8 py-16 text-center" data-id="element-4077" data-mp="17lhtwh16znsi">
              <SparklesIcon className="w-8 h-8 mx-auto mb-4" style={{
            color: `${GOLD}80`
          }} data-id="element-4078" data-mp="1mhz2x1lwsqbw" />
              <p className="text-base text-white/65 mb-5" data-id="element-4079" data-mp="1vcx04kw3ap91">{c.empty}</p>
              <button onClick={() => {
            setQuery('');
            setSector('');
          }} className="px-6 py-2.5 rounded-full text-sm font-bold text-white/75 border border-white/20 transition-colors hover:border-white/40 hover:text-white" data-id="element-4080" data-mp="1ve816cy5hiwg">
                {c.clear}
              </button>
            </div> : <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 items-stretch" data-id="element-4081" data-mp="3o1no1ld9n3x">
                {shown.map((guest, i) => <GuestCard key={guest.id} guest={guest} c={c} priority={i < 4} onRequest={openRequest} data-id="element-4082" data-mp="3vs3uekxokuhu" data-mpi={i} />)}
              </div>
              {hasMore && <div ref={sentinel} aria-hidden className="h-px" data-id="element-4083" data-mp="nbiudtn11bqv" />}
            </>}
        </section>
      </main>

      <footer className="border-t border-white/10 px-6 py-8 text-center" data-id="element-4084" data-mp="ibeea4q16j16">
        <p className="text-xs uppercase tracking-[0.2em] text-white/40" data-id="element-4085" data-mp="dee35febqq37">
          {c.footer}
        </p>
      </footer>

      <AnimatePresence data-id="element-4086" data-mp="333puu0qy1q46">
        {activeGuest && <IntroRequestDialog guest={activeGuest} c={c} onClose={() => setActiveGuest(null)} data-id="element-4087" data-mp="2y43idtixwdwf" />}
      </AnimatePresence>
    </div>;
}